import { Link } from 'react-router';
import { useDashboardSection, SectionContent, Skeleton } from './section';
import { ArrowDownRight, ArrowUpRight, Minus, Plus } from 'lucide-react';
import { fmtAgo, fmtDateTime, displayName } from '@/lib/format';
import { TIERS, type Tier } from '@/types/tiers';
import { Button } from '@/components/ui/button';
import { TierBadge } from '@/components/tier-badge';

type Delta = { current: number; previous: number };
type Dashboard = {
  generatedAt: string;
  audience: {
    total: number;
    users: number;
    leads: number;
    reachable: number;
    suppressed: number;
    bySuppression: Record<string, number>;
    byTier: Record<string, number>;
    byStatus: Record<string, number>;
    active7: number;
    active30: number;
  };
  movement: {
    window: { from: string; to: string };
    signups: Delta & { last7: number };
    upgrade: Delta;
    downgrade: Delta;
    cancel_requested: Delta;
    canceled: Delta;
    payment_failed: Delta;
    trial_ending: Delta;
    profile_completed: Delta;
  };
  attention: { trialsEnding: number; cancelling: number; pastDue: number; upgradeCandidates: number; quietPaid: number; unprofiled: number };
  email: {
    totals: { sent: number; delivered: number; uniqueOpens: number; uniqueClicks: number; unsubscribed: number; bounced: number; complained: number; openRate: number | null; clickRate: number | null };
    daily: Array<{ day: string; sent: number; opened: number; clicked: number; unsubscribed: number }>;
    tests: number;
  };
  campaigns: {
    upcoming: Array<{ id: string; name: string; status: string; scheduledAt: string | null; recipientCount: number }>;
    drafts: number;
    recent: Array<{ id: string; name: string; status: string; completedAt: string | null; recipientCount: number; stats: Record<string, number> | null }>;
  };
  flows: Array<{ id: string; name: string; trigger: { type: string }; running: number; completed: number; failed: number; total: number; enrolled7: number }>;
  segments: Array<{ id: string; name: string; kind: string; memberCount: number; system: boolean; lastEvaluatedAt: string | null }>;
  segmentByName: Record<string, string>;
  feed: Array<{
    id: string;
    type: string;
    occurredAt: string;
    source: string | null;
    payload: Record<string, unknown> | null;
    contact: { id: string; email: string; firstName: string | null; lastName: string | null; tier: string } | null;
  }>;
};

type Overview = Pick<Dashboard, 'generatedAt' | 'audience' | 'movement' | 'attention'>;
type Email = Pick<Dashboard, 'email'>;
type Activity = Pick<Dashboard, 'campaigns' | 'flows' | 'segments' | 'segmentByName' | 'feed'>;

const n = (v: number) => v.toLocaleString();
const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

const EVENT_LABEL: Record<string, string> = {
  upgrade: 'Upgraded',
  downgrade: 'Downgraded',
  cancel_requested: 'Requested cancellation',
  canceled: 'Canceled',
  payment_failed: 'Payment failed',
  trial_ending: 'Trial ending',
  profile_completed: 'Completed profile',
  email_unsubscribed: 'Unsubscribed',
  tag_added: 'Tag added',
  note: 'Note added',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  unknown: 'No subscription',
};

/** "+12%" / "−8%" vs the previous window; invert when a rise is bad (cancels, failures). */
function DeltaBadge({ d, bad = false }: { d: Delta; bad?: boolean }) {
  if (d.previous === 0 && d.current === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const change = d.previous === 0 ? null : Math.round(((d.current - d.previous) / d.previous) * 100);
  const up = d.current > d.previous;
  const good = up ? !bad : bad;
  const tone = d.current === d.previous ? 'text-muted-foreground' : good ? 'text-emerald-600' : 'text-red-600';
  const Icon = d.current === d.previous ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${tone}`} title={`${n(d.previous)} in the previous 30 days`}>
      <Icon className="size-3" />
      {change == null ? 'new' : `${Math.abs(change)}%`}
    </span>
  );
}

function Tile({ label, value, sub, to }: { label: string; value: React.ReactNode; sub?: React.ReactNode; to?: string }) {
  const body = (
    <>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-2xl">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </>
  );
  return to ? (
    <Link to={to} className="block rounded-lg border bg-white p-3 transition-colors hover:border-[var(--ff-primary)]">
      {body}
    </Link>
  ) : (
    <div className="rounded-lg border bg-white p-3">{body}</div>
  );
}

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-white">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <h2 className="text-sm font-medium">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

export function DashboardPage() {
  const overview = useDashboardSection<Overview>('overview');
  const email = useDashboardSection<Email>('email');
  const activity = useDashboardSection<Activity>('activity');
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Facet Flow growth at a glance{overview.data ? ` · synced ${fmtAgo(overview.data.generatedAt)}` : ''} · movement compares the last 30 days with the 30 before.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/templates/new">
              <Plus /> Template
            </Link>
          </Button>
          <Button asChild size="sm" className="bg-[var(--ff-primary)] hover:bg-[var(--ff-primary-hover)]">
            <Link to="/campaigns/new">
              <Plus /> Campaign
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <SectionContent state={overview} label="Overview" errorClassName="col-span-2 md:col-span-3 xl:col-span-5" fallback={<HeadlineSkeleton />}>
          {(data) => <OverviewTiles d={data} />}
        </SectionContent>
        {email.data ? <EmailTile e={email.data.email} /> : (
          <Tile label="Open rate · 30 days" value={email.error ? 'Unavailable' : <Skeleton className="mt-2 h-7 w-20" />} />
        )}
      </div>
      <SectionContent state={overview} label="Overview" showError={false} fallback={<OverviewSkeleton />}>
        {(data) => <OverviewDetails d={data} segmentByName={activity.data?.segmentByName ?? {}} />}
      </SectionContent>
      <Card title="Email · last 30 days" action={<Link to="/analytics" className="ff-link text-xs">Full analytics</Link>}>
        <SectionContent state={email} label="Email performance" fallback={<RowsSkeleton />}>
          {(data) => <EmailDetails e={data.email} />}
        </SectionContent>
      </Card>
      <SectionContent state={activity} label="Campaigns and activity" fallback={<ActivitySkeleton />}>
        {(data) => <ActivityDetails d={data} />}
      </SectionContent>
    </div>
  );
}

function HeadlineSkeleton() {
  return <>{['Users', 'Active in 30 days', 'Paying customers', 'Signups · 30 days', 'Reachable by email'].map((label) => (
    <Tile key={label} label={label} value={<Skeleton className="mt-2 h-7 w-20" />} sub={<Skeleton className="h-3 w-24" />} />
  ))}</>;
}

function RowsSkeleton() {
  return <div role="status" aria-label="Loading dashboard data" className="space-y-4">
    {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
    <span className="sr-only">Loading…</span>
  </div>;
}

function OverviewSkeleton() {
  return <>
    <Card title="Needs attention"><div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
    </div></Card>
    <div className="grid gap-5 lg:grid-cols-2">
      <Card title="Last 30 days"><RowsSkeleton /></Card>
      <Card title="Audience mix"><RowsSkeleton /></Card>
    </div>
  </>;
}

function ActivitySkeleton() {
  return <div className="grid gap-5 lg:grid-cols-2">
    {['Campaigns', 'Active flows', 'Largest segments', 'Recent account events'].map((title) => (
      <Card key={title} title={title}><RowsSkeleton /></Card>
    ))}
  </div>;
}

function OverviewTiles({ d }: { d: Overview }) {
  const { audience: a, movement: m } = d;
  const paid = TIERS.filter((t) => t !== 'free').reduce((sum, t) => sum + (a.byTier[t] ?? 0), 0);
  return <>
        <Tile label="Users" value={n(a.users)} sub={`${n(a.leads)} imported leads`} to="/contacts" />
        <Tile label="Active in 30 days" value={n(a.active30)} sub={`${pct(a.active30, a.users)} of users · ${n(a.active7)} this week`} to="/contacts?sort=lastActivityAt&dir=desc" />
        <Tile label="Paying customers" value={n(paid)} sub={`${pct(paid, a.users)} of users`} to="/contacts?subStatus=active" />
        <Tile
          label="Signups · 30 days"
          value={n(m.signups.current)}
          sub={
            <>
              <DeltaBadge d={m.signups} /> · {n(m.signups.last7)} this week
            </>
          }
          to="/contacts?sort=ffCreatedAt&dir=desc"
        />
        <Tile label="Reachable by email" value={n(a.reachable)} sub={`${n(a.suppressed)} suppressed`} to="/contacts?suppressed=false" />
  </>;
}

function EmailTile({ e }: { e: Dashboard['email'] }) {
  return (
        <Tile
          label="Open rate · 30 days"
          value={e.totals.openRate != null ? `${e.totals.openRate}%` : '—'}
          sub={`${n(e.totals.sent)} sent · ${e.totals.clickRate != null ? `${e.totals.clickRate}% clicked` : 'no clicks yet'}`}
          to="/analytics"
        />  );
}

function OverviewDetails({ d, segmentByName }: { d: Overview; segmentByName: Record<string, string> }) {
  const { audience: a, movement: m, attention: at } = d;
  const seg = (name: string, fallback: string) => (segmentByName[name] ? `/segments/${segmentByName[name]}` : fallback);

  const tierMax = Math.max(1, ...TIERS.map((t) => a.byTier[t] ?? 0));

  const attention: Array<{ label: string; count: number; hint: string; to: string }> = [
    { label: 'Trials ending in 7 days', count: at.trialsEnding, hint: 'Nudge before the trial converts or lapses', to: '/contacts?subStatus=trialing&sort=lastActivityAt&dir=desc' },
    { label: 'Cancelling at period end', count: at.cancelling, hint: 'Still paying — a save offer can still land', to: seg('Cancelling', '/contacts?subStatus=active') },
    { label: 'Payment past due', count: at.pastDue, hint: 'Dunning: card update reminders', to: seg('Payment failed', '/contacts?subStatus=past_due') },
    { label: 'Ready to upgrade', count: at.upgradeCandidates, hint: 'Free, ≥80% credits used, active this month', to: seg('Active free users', '/contacts?tier=free&sort=lastActivityAt&dir=desc') },
    { label: 'Paying but quiet', count: at.quietPaid, hint: 'Paid plan, no activity in 30 days — churn risk', to: '/contacts?subStatus=active&sort=lastActivityAt&dir=asc' },
    { label: 'New, profile incomplete', count: at.unprofiled, hint: 'Signed up in 14 days, never finished onboarding', to: seg('Signed up (last 14 days)', '/contacts?sort=ffCreatedAt&dir=desc') },
  ];

  const movementRows: Array<{ label: string; d: Delta; bad?: boolean; type: string }> = [
    { label: 'Signups', d: m.signups, type: 'signup' },
    { label: 'Profiles completed', d: m.profile_completed, type: 'profile_completed' },
    { label: 'Upgrades', d: m.upgrade, type: 'upgrade' },
    { label: 'Downgrades', d: m.downgrade, bad: true, type: 'downgrade' },
    { label: 'Cancellation requests', d: m.cancel_requested, bad: true, type: 'cancel_requested' },
    { label: 'Canceled', d: m.canceled, bad: true, type: 'canceled' },
    { label: 'Payment failures', d: m.payment_failed, bad: true, type: 'payment_failed' },
    { label: 'Trials ending', d: m.trial_ending, type: 'trial_ending' },
  ];

  return <>
      {/* Needs attention */}
      <Card title="Needs attention">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {attention.map((x) => (
            <Link key={x.label} to={x.to} className="rounded-md border p-3 transition-colors hover:border-[var(--ff-primary)]">
              <div className={`text-2xl ${x.count > 0 ? '' : 'text-muted-foreground'}`}>{n(x.count)}</div>
              <div className="text-sm">{x.label}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{x.hint}</div>
            </Link>
          ))}
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Movement */}
        <Card title="Last 30 days" action={<Link to="/audit" className="ff-link text-xs">Audit log</Link>}>
          <ul className="divide-y">
            {movementRows.map((r) => (
              <li key={r.type} className="flex items-center justify-between py-1.5 text-sm">
                <Link to={`/audit?type=${r.type}`} className="hover:underline">
                  {r.label}
                </Link>
                <span className="flex items-center gap-3">
                  <DeltaBadge d={r.d} bad={r.bad} />
                  <span className="w-12 text-right tabular-nums">{n(r.d.current)}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Audience mix */}
        <Card title="Audience mix">
          <div className="space-y-1.5">
            {TIERS.map((t: Tier) => {
              const count = a.byTier[t] ?? 0;
              return (
                <Link key={t} to={`/contacts?tier=${t}`} className="flex items-center gap-3 text-sm">
                  <span className="w-24 shrink-0">
                    <TierBadge tier={t} />
                  </span>
                  <span className="h-2 flex-1 overflow-hidden rounded bg-muted">
                    <span className="block h-full bg-[var(--ff-primary)]" style={{ width: `${(count / tierMax) * 100}%` }} />
                  </span>
                  <span className="w-20 text-right tabular-nums">
                    {n(count)} <span className="text-xs text-muted-foreground">{pct(count, a.users)}</span>
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {Object.entries(a.byStatus)
              .sort((x, y) => y[1] - x[1])
              .map(([s, count]) => (
                <Link key={s} to={s === 'unknown' ? '/contacts' : `/contacts?subStatus=${s}`} className="rounded bg-muted px-2 py-1 hover:bg-accent">
                  {STATUS_LABEL[s] ?? s} <span className="text-muted-foreground">{n(count)}</span>
                </Link>
              ))}
            {Object.entries(a.bySuppression).map(([r, count]) => (
              <Link key={r} to="/contacts?suppressed=true" className="rounded bg-red-50 px-2 py-1 text-red-700 hover:bg-red-100">
                {r} <span className="opacity-70">{n(count)}</span>
              </Link>
            ))}
          </div>
        </Card>
      </div>

  </>;
}

function EmailDetails({ e }: { e: Dashboard['email'] }) {
  const dailyMax = Math.max(1, ...e.daily.map((x) => x.sent));
  return (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {(
              [
                ['Sent', n(e.totals.sent)],
                ['Delivered', `${n(e.totals.delivered)} · ${pct(e.totals.delivered, e.totals.sent)}`],
                ['Unique opens', `${n(e.totals.uniqueOpens)}${e.totals.openRate != null ? ` · ${e.totals.openRate}%` : ''}`],
                ['Unique clicks', `${n(e.totals.uniqueClicks)}${e.totals.clickRate != null ? ` · ${e.totals.clickRate}%` : ''}`],
                ['Unsubscribed', n(e.totals.unsubscribed)],
                ['Bounced · complained', `${n(e.totals.bounced)} · ${n(e.totals.complained)}`],
              ] as Array<[string, string]>
            ).map(([k, v]) => (
              <div key={k}>
                <div className="text-xs text-muted-foreground">{k}</div>
                <div>{v}</div>
              </div>
            ))}
            <div className="col-span-2 text-xs text-muted-foreground">{n(e.tests)} test sends excluded</div>
          </div>
          <div>
            {e.daily.length === 0 ? (
              <Empty>Nothing sent in the last 30 days.</Empty>
            ) : (
              <div className="flex h-28 items-end gap-px">
                {e.daily.map((x) => (
                  <div key={x.day} className="flex flex-1 flex-col justify-end gap-px" title={`${x.day}: ${x.sent} sent, ${x.opened} opens, ${x.clicked} clicks`}>
                    <div className="bg-[var(--ff-primary)]" style={{ height: `${(x.sent / dailyMax) * 100}%` }} />
                    <div className="bg-sky-400" style={{ height: `${(x.opened / dailyMax) * 100}%` }} />
                    <div className="bg-emerald-500" style={{ height: `${(x.clicked / dailyMax) * 100}%` }} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
              <span><span className="inline-block size-2 bg-[var(--ff-primary)]" /> sent</span>
              <span><span className="inline-block size-2 bg-sky-400" /> opens</span>
              <span><span className="inline-block size-2 bg-emerald-500" /> clicks</span>
            </div>
          </div>
        </div>
  );
}

function ActivityDetails({ d }: { d: Activity }) {
  return <>
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Campaigns */}
        <Card title="Campaigns" action={<Link to="/campaigns" className="ff-link text-xs">All campaigns</Link>}>
          {d.campaigns.upcoming.length === 0 && d.campaigns.recent.length === 0 ? (
            <Empty>
              No campaigns yet — <Link to="/campaigns/new" className="ff-link">create the first newsletter</Link>.
            </Empty>
          ) : (
            <div className="space-y-4 text-sm">
              {d.campaigns.upcoming.length > 0 && (
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Scheduled / sending</div>
                  <ul className="divide-y">
                    {d.campaigns.upcoming.map((c) => (
                      <li key={c.id} className="flex items-center justify-between py-1.5">
                        <Link to={`/campaigns/${c.id}`} className="ff-link truncate">{c.name}</Link>
                        <span className="ml-3 shrink-0 text-xs text-muted-foreground">
                          {c.status === 'sending' ? 'sending now' : c.scheduledAt ? fmtDateTime(c.scheduledAt) : 'scheduled'} · {n(c.recipientCount)} recipients
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.campaigns.recent.length > 0 && (
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Recently sent</div>
                  <ul className="divide-y">
                    {d.campaigns.recent.map((c) => (
                      <li key={c.id} className="flex items-center justify-between py-1.5">
                        <Link to={`/campaigns/${c.id}`} className="ff-link truncate">{c.name}</Link>
                        <span className="ml-3 shrink-0 text-xs text-muted-foreground">
                          {c.status !== 'sent' ? `${c.status} · ` : ''}
                          {n(c.stats?.sent ?? c.recipientCount)} sent
                          {c.stats?.opened != null ? ` · ${n(c.stats.opened)} opened` : ''}
                          {c.completedAt ? ` · ${fmtAgo(c.completedAt)}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.campaigns.drafts > 0 && (
                <div className="text-xs text-muted-foreground">
                  {n(d.campaigns.drafts)} draft{d.campaigns.drafts === 1 ? '' : 's'} waiting.
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Flows */}
        <Card title="Active flows" action={<Link to="/flows" className="ff-link text-xs">All flows</Link>}>
          {d.flows.length === 0 ? (
            <Empty>
              No flow is active — the starter flows are drafts. <Link to="/flows" className="ff-link">Review and activate one</Link>.
            </Empty>
          ) : (
            <ul className="divide-y text-sm">
              {d.flows.map((f) => (
                <li key={f.id} className="flex items-center justify-between py-1.5">
                  <div className="min-w-0">
                    <Link to={`/flows/${f.id}`} className="ff-link">{f.name}</Link>
                    <div className="text-xs text-muted-foreground">on {f.trigger?.type?.replace(/_/g, ' ')}</div>
                  </div>
                  <div className="ml-3 shrink-0 text-right text-xs text-muted-foreground">
                    <div>{n(f.running)} in progress · {n(f.completed)} completed{f.failed ? ` · ${n(f.failed)} failed` : ''}</div>
                    <div>{n(f.enrolled7)} enrolled this week</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Segments */}
        <Card title="Largest segments" action={<Link to="/segments" className="ff-link text-xs">All segments</Link>}>
          {d.segments.length === 0 ? (
            <Empty>No segments yet.</Empty>
          ) : (
            <ul className="divide-y text-sm">
              {d.segments.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-1.5">
                  <Link to={`/segments/${s.id}`} className="ff-link truncate">{s.name}</Link>
                  <span className="ml-3 shrink-0 tabular-nums">
                    {n(s.memberCount)} <span className="text-xs text-muted-foreground">{s.kind}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Feed */}
        <Card title="Recent account events" action={<Link to="/audit" className="ff-link text-xs">Audit log</Link>}>
          {d.feed.length === 0 ? (
            <Empty>Nothing notable yet — upgrades, cancellations and payment failures show up here as the sync sees them.</Empty>
          ) : (
            <ul className="divide-y text-sm">
              {d.feed.map((ev) => (
                <li key={ev.id} className="flex items-center justify-between gap-3 py-1.5">
                  <div className="min-w-0 truncate">
                    <span className="font-medium">{EVENT_LABEL[ev.type] ?? ev.type}</span>
                    {ev.contact && (
                      <>
                        {' · '}
                        <Link to={`/contacts/${ev.contact.id}`} className="ff-link">{displayName(ev.contact)}</Link>
                        <span className="ml-1.5 align-middle"><TierBadge tier={ev.contact.tier} /></span>
                      </>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground" title={fmtDateTime(ev.occurredAt)}>{fmtAgo(ev.occurredAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
  </>;
}
